import React, { useEffect, useState } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { CATEGORY_ICONS, CATEGORY_TINTS } from "@/lib/categories";
import {
  ArrowRight, Inbox, Users, Wallet, Sparkles, CheckCircle2, BarChart3,
  ShieldCheck, MessageCircleHeart, Building2, Clock, Star,
} from "lucide-react";

const VoorKantoren = () => {
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    api.get("/service-categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const totalServices = categories.reduce((acc, c) => acc + c.subcategories.length, 0);

  return (
    <div className="min-h-screen flex flex-col">
      <Header />

      {/* HERO */}
      <section className="relative">
        <div className="absolute inset-0 -z-10 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-amber-50/60 via-white to-white" />
          <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-[#F5A623]/15 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full bg-[#1A2B4A]/10 blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-14 md:pt-20 pb-12 md:pb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="pill-amber" data-testid="vk-badge">{t("vk.badge")}</span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold text-[#1A2B4A] mt-4 leading-[1.05] tracking-tight">
                {t("vk.hero_1")}<br /><span className="text-[#F5A623]">{t("vk.hero_highlight")}</span>{t("vk.hero_2")}
              </h1>
              <p className="text-lg text-slate-600 mt-5 max-w-xl">{t("vk.hero_sub")}</p>

              <div className="mt-7 flex flex-col sm:flex-row gap-3">
                <Link to="/aanmelden?role=kantoor" className="btn-amber" data-testid="vk-cta-register">
                  {t("vk.cta_register")} <ArrowRight size={16} />
                </Link>
                <Link to="/login?role=kantoor" className="btn-outline-navy" data-testid="vk-cta-login">
                  {t("vk.cta_login")}
                </Link>
              </div>

              <div className="mt-7 flex flex-wrap gap-5 text-sm text-slate-700">
                <span className="flex items-center gap-2"><Wallet size={16} className="text-[#22C55E]" /> {t("vk.no_subscription")}</span>
                <span className="flex items-center gap-2"><ShieldCheck size={16} className="text-[#22C55E]" /> {t("vk.only_pay_match")}</span>
              </div>
            </div>

            <div className="relative">
              <div className="beecenter-card overflow-hidden">
                <img
                  src="https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                  alt="Professional team"
                  className="w-full h-72 md:h-[420px] object-cover"
                />
              </div>
              {/* Floating stat card */}
              <div className="absolute -bottom-5 left-4 right-4 md:-bottom-6 md:left-8 md:right-auto md:w-auto bg-white rounded-2xl shadow-xl ring-1 ring-slate-100 p-4 flex items-center gap-3">
                <div className="w-11 h-11 rounded-xl bg-[#F5A623]/15 text-[#F5A623] flex items-center justify-center flex-shrink-0">
                  <Inbox size={20} />
                </div>
                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 font-bold">{t("vk.stat_card_label")}</p>
                  <p className="font-heading font-bold text-[#1A2B4A]">{t("vk.stat_card_value")}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-[#1A2B4A] text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-10 md:py-12">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { v: `${totalServices}+`, l: t("vk.stat_services") },
              { v: "200+", l: t("vk.stat_entrepreneurs") },
              { v: t("vk.stat_fee_value"), l: t("vk.stat_fee") },
              { v: "0", l: t("vk.stat_zero_sub") },
            ].map((s, i) => (
              <div key={i} className="text-center" data-testid={`vk-stat-${i}`}>
                <div className="font-heading text-3xl md:text-5xl font-extrabold text-[#F5A623] tracking-tight">{s.v}</div>
                <div className="text-xs uppercase tracking-wider text-slate-300 mt-2">{s.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="pill-amber">{t("vk.benefits_badge")}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2B4A] mt-3 tracking-tight">{t("vk.benefits_title")}</h2>
            <p className="text-slate-600 mt-4">{t("vk.benefits_subtitle")}</p>
          </div>
          <div className="mt-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Users, t: t("vk.b1_t"), d: t("vk.b1_d"), tint: "bg-amber-50 text-[#F5A623] border-amber-200" },
              { icon: Wallet, t: t("vk.b2_t"), d: t("vk.b2_d"), tint: "bg-emerald-50 text-emerald-600 border-emerald-200" },
              { icon: BarChart3, t: t("vk.b3_t"), d: t("vk.b3_d"), tint: "bg-blue-50 text-blue-600 border-blue-200" },
              { icon: ShieldCheck, t: t("vk.b4_t"), d: t("vk.b4_d"), tint: "bg-indigo-50 text-indigo-600 border-indigo-200" },
              { icon: MessageCircleHeart, t: t("vk.b5_t"), d: t("vk.b5_d"), tint: "bg-rose-50 text-rose-600 border-rose-200" },
              { icon: Sparkles, t: t("vk.b6_t"), d: t("vk.b6_d"), tint: "bg-purple-50 text-purple-600 border-purple-200" },
            ].map((b, i) => {
              const Icon = b.icon;
              return (
                <div key={i} className="beecenter-card p-6 hover:shadow-md transition-all" data-testid={`vk-benefit-${i}`}>
                  <div className={`w-12 h-12 rounded-xl ${b.tint} border flex items-center justify-center`}>
                    <Icon size={22} />
                  </div>
                  <h3 className="font-heading font-bold text-lg text-[#1A2B4A] mt-4">{b.t}</h3>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{b.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS — provider side */}
      <section className="bg-[#F8FAFC] py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="pill-amber">{t("vk.how_badge")}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2B4A] mt-3 tracking-tight">{t("vk.how_title")}</h2>
            <p className="text-slate-600 mt-4">{t("vk.how_subtitle")}</p>
          </div>
          <div className="mt-12 grid md:grid-cols-4 gap-5">
            {[
              { n: "01", icon: Building2, t: t("vk.h1_t"), d: t("vk.h1_d") },
              { n: "02", icon: Inbox, t: t("vk.h2_t"), d: t("vk.h2_d") },
              { n: "03", icon: MessageCircleHeart, t: t("vk.h3_t"), d: t("vk.h3_d") },
              { n: "04", icon: CheckCircle2, t: t("vk.h4_t"), d: t("vk.h4_d") },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.n} className="beecenter-card p-6 relative" data-testid={`vk-step-${s.n}`}>
                  <span className="absolute top-4 right-4 text-[#F5A623] font-heading font-extrabold text-xl opacity-60">{s.n}</span>
                  <div className="w-11 h-11 rounded-xl bg-[#1A2B4A] text-white flex items-center justify-center">
                    <Icon size={20} />
                  </div>
                  <h3 className="font-heading font-bold text-[#1A2B4A] mt-4">{s.t}</h3>
                  <p className="text-sm text-slate-600 mt-2 leading-relaxed">{s.d}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CATEGORIES — which categories can join */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <span className="pill-amber">{t("vk.cats_badge")}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2B4A] mt-3 tracking-tight">{t("vk.cats_title")}</h2>
            <p className="text-slate-600 mt-4">{t("vk.cats_subtitle")}</p>
          </div>
          <div className="mt-10 grid sm:grid-cols-2 md:grid-cols-3 gap-3">
            {categories.map((c) => {
              const Icon = CATEGORY_ICONS[c.icon] || Sparkles;
              const tint = CATEGORY_TINTS[c.color] || CATEGORY_TINTS.slate;
              return (
                <div key={c.id} className="flex items-center gap-3 p-4 rounded-xl bg-white border border-slate-200" data-testid={`vk-cat-${c.id}`}>
                  <div className={`w-10 h-10 rounded-lg ${tint.bg} ${tint.text} ${tint.border} border flex items-center justify-center flex-shrink-0`}>
                    <Icon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-heading font-bold text-sm text-[#1A2B4A] truncate">{c.naam}</p>
                    <p className="text-xs text-slate-500">{c.subcategories.length} {t("services.count_suffix")}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="bg-[#F8FAFC] py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8">
          <div className="text-center max-w-xl mx-auto">
            <span className="pill-amber">{t("vk.pricing_badge")}</span>
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2B4A] mt-3 tracking-tight">{t("vk.pricing_title")}</h2>
          </div>
          <div className="mt-10 beecenter-card p-8 md:p-10 bg-white border-2 border-[#F5A623]/30" data-testid="vk-pricing-card">
            <div className="flex items-baseline gap-2 flex-wrap">
              <span className="font-heading text-5xl md:text-6xl font-extrabold text-[#1A2B4A]">{t("vk.pricing_amount")}</span>
              <span className="text-slate-500">{t("vk.pricing_per_match")}</span>
            </div>
            <p className="text-slate-600 mt-3">{t("vk.pricing_desc")}</p>
            <div className="mt-7 grid sm:grid-cols-2 gap-3">
              {[
                t("vk.pricing_b1"), t("vk.pricing_b2"), t("vk.pricing_b3"),
                t("vk.pricing_b4"), t("vk.pricing_b5"), t("vk.pricing_b6"),
              ].map((b, i) => (
                <div key={i} className="flex items-start gap-2 text-sm text-slate-700">
                  <CheckCircle2 size={16} className="text-[#22C55E] flex-shrink-0 mt-0.5" />
                  <span>{b}</span>
                </div>
              ))}
            </div>
            <div className="mt-7 pt-6 border-t border-slate-100 flex flex-col sm:flex-row gap-3 items-center justify-between">
              <div className="flex items-center gap-3 text-sm">
                <Clock size={16} className="text-[#F5A623]" />
                <span className="text-slate-600">{t("vk.pricing_signup_time")}</span>
              </div>
              <Link to="/aanmelden?role=kantoor" className="btn-amber whitespace-nowrap" data-testid="vk-pricing-cta">
                {t("vk.pricing_cta")} <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 md:py-20">
        <div className="max-w-3xl mx-auto px-4 md:px-8">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-[#1A2B4A] tracking-tight">{t("vk.faq_title")}</h2>
          <div className="mt-8 space-y-3">
            {[
              { q: t("vk.faq_q1"), a: t("vk.faq_a1") },
              { q: t("vk.faq_q2"), a: t("vk.faq_a2") },
              { q: t("vk.faq_q3"), a: t("vk.faq_a3") },
              { q: t("vk.faq_q4"), a: t("vk.faq_a4") },
              { q: t("vk.faq_q5"), a: t("vk.faq_a5") },
            ].map((f, i) => (
              <details key={i} className="beecenter-card group" data-testid={`vk-faq-${i}`}>
                <summary className="cursor-pointer list-none p-5 flex items-center justify-between gap-4">
                  <span className="font-heading font-semibold text-[#1A2B4A]">{f.q}</span>
                  <span className="w-8 h-8 rounded-full bg-slate-100 group-hover:bg-[#F5A623]/10 flex items-center justify-center text-slate-500 group-open:rotate-45 transition-all">+</span>
                </summary>
                <div className="px-5 pb-5 -mt-2 text-sm text-slate-600 leading-relaxed">{f.a}</div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#1A2B4A] text-white py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-5xl font-bold tracking-tight">{t("vk.cta_title")}</h2>
          <p className="text-slate-300 mt-4 text-lg">{t("vk.cta_subtitle")}</p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/aanmelden?role=kantoor" className="btn-amber" data-testid="vk-final-cta-register">
              {t("vk.cta_register")} <ArrowRight size={16} />
            </Link>
            <Link to="/login?role=kantoor" className="btn-outline-navy !text-white !border-white hover:!bg-white hover:!text-[#1A2B4A]" data-testid="vk-final-cta-login">
              {t("vk.cta_login")}
            </Link>
          </div>
          <div className="mt-10 flex items-center justify-center gap-2 text-sm text-slate-300">
            <div className="flex">{[1,2,3,4,5].map((s) => <Star key={s} size={14} className="fill-[#F5A623] text-[#F5A623]" />)}</div>
            <span className="font-semibold text-white">4.9/5</span>
            <span>·</span>
            <span>{t("vk.cta_trust")}</span>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default VoorKantoren;
